import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  ChevronRight, ChevronDown, ChevronUp, Edit2, Trash2, FileText, Image as ImageIcon,
  Video, Link, Sparkles, Layers, ExternalLink, X, ChevronLeft, ChevronRight as ChevronRightIcon, Upload,
  Maximize2, Minimize2, MoreHorizontal, Highlighter, CheckSquare, Square, History, Download, LockKeyhole, LockKeyholeOpen,
} from 'lucide-react';
import { API, useAuth } from '../App';
import CardVersionHistory from './CardVersionHistory';

const TYPE_META = {
  gateway: { icon: Layers,     label: 'Gateway',   color: '#6366f1' },
  text:    { icon: FileText,   label: 'Text',       color: '#06b6d4' },
  document:{ icon: FileText,   label: 'File',       color: '#06b6d4' },
  image:   { icon: ImageIcon,  label: 'Image',      color: '#10b981' },
  video:   { icon: Video,      label: 'Video',      color: '#f59e0b' },
  link:    { icon: Link,       label: 'Link',       color: '#8b5cf6' },
  ai:      { icon: Sparkles,   label: 'AI Output',  color: '#ec4899' },
};
const COMPACT_PREVIEW_HEIGHT = 100;
const COMPACT_SUMMARY_WORD_LIMIT = 36;

function compactSummaryPreview(summary) {
  const words = summary.trim().split(/\s+/);
  if (words.length <= COMPACT_SUMMARY_WORD_LIMIT) return summary;
  const excerpt = words.slice(0, COMPACT_SUMMARY_WORD_LIMIT).join(' ').replace(/[,;:–—-]$/, '');
  return /[.!?]$/.test(excerpt) ? excerpt : `${excerpt}…`;
}


// Darken a hex color by a factor (0=black, 1=same)
function darkenHex(hex, factor = 0.55) {
  if (!hex) return hex;
  const h = hex.replace('#', '');
  const r = Math.round(parseInt(h.slice(0,2),16) * factor);
  const g = Math.round(parseInt(h.slice(2,4),16) * factor);
  const b = Math.round(parseInt(h.slice(4,6),16) * factor);
  return `rgb(${r},${g},${b})`;
}
const HIGHLIGHT_COLORS = [
  { label: 'None',   value: null,      swatch: 'transparent', border: 'var(--border)' },
  { label: 'Yellow', value: '#fef08a', swatch: '#fef08a',     border: '#ca8a04' },
  { label: 'Green',  value: '#bbf7d0', swatch: '#bbf7d0',     border: '#16a34a' },
  { label: 'Blue',   value: '#bfdbfe', swatch: '#bfdbfe',     border: '#2563eb' },
  { label: 'Pink',   value: '#fbcfe8', swatch: '#fbcfe8',     border: '#db2777' },
  { label: 'Orange', value: '#fed7aa', swatch: '#fed7aa',     border: '#ea580c' },
  { label: 'Purple', value: '#e9d5ff', swatch: '#e9d5ff',     border: '#9333ea' },
];

// Cache OG fetches
const ogCache = {};
function useLinkPreview(url) {
  const [preview, setPreview] = useState(ogCache[url] || null);
  useEffect(() => {
    if (!url || preview) return;
    fetch(`${API}/api/og`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ url }) })
      .then(r => r.json()).then(d => { ogCache[url] = d; setPreview(d); }).catch(() => {});
  }, [url]);
  return preview;
}

export default function BoxCard({ box, depth, index, color, isMobile, onDrillIn, onEdit, onDelete, onUpdate, onToggleSpan, onAskAI, canSetPrivate = false, dragHandleProps, hideZoomIn, compactView = false, isCurrentLevel = false, lockedBy }) {
  const { token } = useAuth();
  const clickTimerRef = useRef(null);
  const lastFileClickRef = useRef({ action: null, time: 0 });
  const cardRef = useRef(null);
  const onDrillInRef = useRef(onDrillIn);
  const onEditRef = useRef(onEdit);
  useEffect(() => { onDrillInRef.current = onDrillIn; }, [onDrillIn]);
  useEffect(() => { onEditRef.current = onEdit; }, [onEdit]);

  // Native DOM events — Framer Motion eats synthetic dblclick
  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    function handleClick(e) {
      // Don't drill in if click came from a button, input, anchor, or any
      // element marked data-no-drill — these handle their own click logic
      const interactive = e.target.closest('button, a, input, textarea, select, [data-no-drill]');
      if (interactive || !onDrillInRef.current) return;
      clearTimeout(clickTimerRef.current);
      clickTimerRef.current = setTimeout(() => onDrillInRef.current?.(), 250);
    }
    function handleDblClick(e) {
      // File controls (and all other interactive card content) own their clicks.
      if (e.target.closest('button, a, input, textarea, select, [data-no-drill]')) return;
      e.stopPropagation();
      clearTimeout(clickTimerRef.current);
      onEditRef.current?.();
    }
    el.addEventListener('click', handleClick);
    el.addEventListener('dblclick', handleDblClick);
    return () => {
      el.removeEventListener('click', handleClick);
      el.removeEventListener('dblclick', handleDblClick);
      clearTimeout(clickTimerRef.current);
    };
  }, []); // empty deps — callbacks accessed via refs
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [cardDragOver, setCardDragOver] = useState(false);
  const [lightbox, setLightbox] = useState(null); // { images: [...], index: n }
  const [uploading, setUploading] = useState(false);
  const [fileError, setFileError] = useState('');
  const [privacyError, setPrivacyError] = useState('');
  const [pdfThumbnail, setPdfThumbnail] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, right: 0 });
  const menuRef = useRef(null);
  const menuBtnRef = useRef(null);
  const previewRef = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [canExpand, setCanExpand] = useState(false);
  const images = Array.isArray(box.images) ? box.images : [];
  const canSummarize = !['image', 'document'].includes(box.content_type) && !!String(box.content || '')
    .replace(/!\[[^\]]*\]\([^)]*\)/g, ' ').replace(/<[^>]+>/g, ' ').trim();

  useEffect(() => { setExpanded(false); }, [compactView]);
  useEffect(() => {
    if (box.content_type !== 'document' || !/\.pdf$/i.test(box.content || '') || !token) return;
    let active = true;
    let previewUrl;
    setPdfThumbnail(null);
    fetch(`${API}/api/boxes/${box.id}/thumbnail`, { headers: { Authorization: `Bearer ${token}` } })
      .then(async response => {
        if (!response.ok) return;
        const url = URL.createObjectURL(await response.blob());
        if (active) { previewUrl = url; setPdfThumbnail(url); }
        else URL.revokeObjectURL(url);
      }).catch(() => {});
    return () => { active = false; if (previewUrl) URL.revokeObjectURL(previewUrl); };
  }, [box.id, box.content, box.content_type, token]);
  useEffect(() => {
    if (box.content_type === 'document') { setCanExpand(false); return; }
    if (compactView && canSummarize) { setCanExpand(true); return; }
    if (!compactView || !previewRef.current) { setCanExpand(false); return; }
    const preview = previewRef.current;
    const measure = () => setCanExpand(preview.getBoundingClientRect().height > COMPACT_PREVIEW_HEIGHT);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(preview);
    return () => observer.disconnect();
  }, [compactView, canSummarize, box.content, box.content_type, box.images, box.childPreviews]);

  // Close menu on outside click or scroll
  useEffect(() => {
    if (!menuOpen) return;
    function onOutside(e) {
      if (menuRef.current && !menuRef.current.contains(e.target) &&
          menuBtnRef.current && !menuBtnRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', onOutside);
    document.addEventListener('scroll', () => setMenuOpen(false), true);
    return () => {
      document.removeEventListener('mousedown', onOutside);
      document.removeEventListener('scroll', () => setMenuOpen(false), true);
    };
  }, [menuOpen]);

  const openMenu = useCallback((e) => {
    e.stopPropagation();
    if (!menuOpen && menuBtnRef.current) {
      const rect = menuBtnRef.current.getBoundingClientRect();
      // Estimate menu height (highlight row + up to 4 menu items) to decide whether
      // it fits below the button or needs to flip upward near the bottom of the screen.
      const estimatedMenuHeight = 300;
      const spaceBelow = window.innerHeight - rect.bottom;
      const shouldFlipUp = spaceBelow < estimatedMenuHeight && rect.top > estimatedMenuHeight;
      setMenuPos(
        shouldFlipUp
          ? { bottom: window.innerHeight - rect.top + 6, right: window.innerWidth - rect.right }
          : { top: rect.bottom + 6, right: window.innerWidth - rect.right }
      );
    }
    setMenuOpen(o => !o);
  }, [menuOpen]);

  async function handleToggleTodo() {
    setMenuOpen(false);
    const newVal = !box.is_todo;
    const res = await fetch(`${API}/api/boxes/${box.id}/todo`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ is_todo: newVal }),
    });
    const updated = await res.json();
    if (onUpdate) onUpdate({ ...box, ...updated, images: Array.isArray(box.images) ? box.images : [] });
  }

  async function handleHighlight(colorValue) {
    setMenuOpen(false);
    const res = await fetch(`${API}/api/boxes/${box.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ color: colorValue === null ? null : colorValue }),
    });
    const updated = await res.json();
    if (onUpdate) onUpdate({ ...updated, images: Array.isArray(updated.images) ? updated.images : JSON.parse(updated.images || '[]') });
  }

  async function handleTogglePrivate() {
    setMenuOpen(false);
    setPrivacyError('');
    try {
      const response = await fetch(`${API}/api/boxes/${box.id}/privacy`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ is_private: !box.is_private }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Could not change card privacy');
      onUpdate?.(result);
    } catch (error) { setPrivacyError(error.message); }
  }

  const meta      = TYPE_META[box.content_type] || TYPE_META.gateway;
  const TypeIcon  = meta.icon;
  const accent    = isCurrentLevel ? '#16a34a' : box.color || color;
  const isLink    = box.content_type === 'link';
  const showActions = isMobile || hovered;
  const showCompactSummary = compactView && !expanded && canSummarize;
  const previewClipped = compactView && !expanded && !canSummarize && box.content_type !== 'document';

  const linkPreview = useLinkPreview(isLink ? box.content : null);

  async function openCardFile(download = false) {
    const action = download ? 'download' : 'open';
    if (lastFileClickRef.current.action === action && Date.now() - lastFileClickRef.current.time < 650) return;
    lastFileClickRef.current = { action, time: Date.now() };
    setFileError('');
    const tab = download ? null : window.open('about:blank', '_blank');
    if (!download && !tab) { setFileError('Allow pop-ups to open this file, or use Download.'); return; }
    try {
      const response = await fetch(`${API}/api/boxes/${box.id}/file${download ? '?download=1' : ''}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error((await response.json()).error || 'Could not open file');
      const url = URL.createObjectURL(await response.blob());
      if (download) {
        const link = document.createElement('a');
        link.href = url;
        link.download = box.content || box.title || 'file';
        document.body.appendChild(link); link.click(); link.remove();
      } else tab.location.href = url;
      setTimeout(() => URL.revokeObjectURL(url), 300000);
    } catch (error) { tab?.close(); setFileError(error.message); }
  }

  function handleDelete(e) {
    e.stopPropagation();
    if (isMobile) { onDelete(); return; }
    setShowDeleteModal(true);
  }

  // ── Card-level image drop ──────────────────────────────────────────────────
  function onCardDragOver(e) {
    if (!token) return; // not logged in — no drop UI
    if (uploading) return; // upload in progress — don't re-show overlay
    const types = [...e.dataTransfer.types];
    if (types.includes('Files') || types.includes('text/x-ideacanvas-image-url')) {
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = 'copy';
      setCardDragOver(true);
    }
  }
  function onCardDragLeave(e) {
    // only clear if leaving the card itself
    if (!e.currentTarget.contains(e.relatedTarget)) setCardDragOver(false);
  }
  async function onCardDrop(e) {
    if (!token) return; // not logged in — ignore drop
    const draggedUrl = e.dataTransfer.getData('text/x-ideacanvas-image-url');
    const droppedFiles = [...e.dataTransfer.files];
    const imageFiles = droppedFiles.filter(f => f.type.startsWith('image/'));
    // Documents bubble to the canvas, where they create file-backed cards.
    if (!draggedUrl && droppedFiles.length !== imageFiles.length) { setCardDragOver(false); return; }
    e.preventDefault();
    e.stopPropagation();
    setCardDragOver(false);
    const newImages = [...images];

    // Dragged URL from AI image search results
    if (draggedUrl) {
      newImages.push(draggedUrl);
    } else {
      // File drop from desktop
      const files = imageFiles;
      if (!files.length) return;
      setUploading(true);
      for (const file of files) {
        const form = new FormData(); form.append('file', file);
        try {
          const { url } = await fetch(`${API}/api/upload`, { method: 'POST', body: form, headers: token ? { Authorization: `Bearer ${token}` } : {} }).then(r => r.json());
          const absUrl = url.startsWith('/') ? `${API}${url}` : url;
          newImages.push(absUrl);
        } catch (err) { console.error('[BoxCard] Upload failed', err); }
      }
    }
    if (!newImages.length) { setUploading(false); return; }
    // Persist to server
    const res = await fetch(`${API}/api/boxes/${box.id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ images: newImages }),
    });
    const updated = await res.json();
    setUploading(false);
    // Tell parent canvas to update this box in state — triggers proper re-render
    if (onUpdate) onUpdate({ ...updated, images: newImages });
  }

  // ── Lightbox keyboard nav ──────────────────────────────────────────────────
  useEffect(() => {
    if (!lightbox) return;
    const handler = (e) => {
      if (e.key === 'Escape') setLightbox(null);
      if (e.key === 'ArrowRight') setLightbox(l => l && l.index < l.images.length - 1 ? { ...l, index: l.index + 1 } : l);
      if (e.key === 'ArrowLeft')  setLightbox(l => l && l.index > 0 ? { ...l, index: l.index - 1 } : l);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lightbox]);

  const renderContent = () => {
    if (!box.content) return null;
    switch (box.content_type) {
      case 'document': {
        const viewable = /\.(pdf|txt|md|csv|tsv|json|jsonl|yaml|yml|log)$/i.test(box.content);
        return <div data-no-drill style={{ marginTop: 10, fontSize: 12, color: 'var(--text-muted)' }}>
          {pdfThumbnail && token && <button type="button" onClick={() => openCardFile()} title={`Open ${box.content}`} aria-label={`Open ${box.content}`} style={{ display: 'block', maxWidth: '100%', padding: 0, border: 0, background: 'transparent', cursor: 'pointer', marginBottom: 9 }}>
            <img src={pdfThumbnail} alt={`First page of ${box.content}`} style={{ display: 'block', maxWidth: '100%', width: compactView ? 192 : 280, maxHeight: compactView ? 260 : 380, objectFit: 'contain', border: '1px solid var(--border)', borderRadius: 5, background: '#fff' }} />
          </button>}
          <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', alignItems: 'center' }}>
            <FileText size={15} /> <span style={{ overflowWrap: 'anywhere' }}>{box.content}</span>
          </div>
          {token ? <div style={{ display: 'flex', gap: 7, marginTop: 9 }}>
            {viewable && <button type="button" onClick={() => openCardFile()} style={fileButton}>Open</button>}
            <button type="button" onClick={() => openCardFile(true)} style={fileButton}><Download size={12} /> Download</button>
          </div> : <div style={{ marginTop: 6 }}>Sign in to access this attachment.</div>}
          {fileError && <div role="alert" style={{ color: '#ef4444', marginTop: 7 }}>{fileError}</div>}
        </div>;
      }
      case 'gateway':
      case 'text': {
        const isHtml = box.content && box.content.trimStart().startsWith('<');
        return (
          <div className="prose" style={{ fontSize: 13.5, marginTop: 10, lineHeight: 1.75 }}
            onClick={e => {
              const a = e.target.closest('a');
              if (a) { e.stopPropagation(); window.open(a.href, '_blank', 'noopener,noreferrer'); e.preventDefault(); }
            }}>
            {isHtml
              ? <div className="prose" dangerouslySetInnerHTML={{ __html: box.content }} style={{ maxWidth: '100%' }} />
              : <ReactMarkdown remarkPlugins={[remarkGfm]}>{box.content}</ReactMarkdown>
            }
          </div>
        );
      }
      case 'ai':
        return (
          <div style={{ marginTop: 10 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '2px 8px', borderRadius: 5, marginBottom: 10, background: `${accent}18`, border: `1px solid ${accent}30` }}>
              <Sparkles size={10} color={accent} />
              <span style={{ fontSize: 10, color: accent, fontWeight: 600, letterSpacing: '0.4px' }}>AI GENERATED</span>
            </div>
            <div className="prose" style={{ fontSize: 13.5, lineHeight: 1.75 }}
              onClick={e => {
                const a = e.target.closest('a');
                if (a) { e.stopPropagation(); window.open(a.href, '_blank', 'noopener,noreferrer'); e.preventDefault(); }
              }}>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{box.content}</ReactMarkdown>
            </div>
          </div>
        );
      case 'image': {
        // Defensive: content should be a bare URL, but a stray full <img src="..."> tag
        // sometimes ends up here (e.g. from AI card creation). Unwrap it so the image
        // still renders instead of showing a broken image with an escaped tag as the URL.
        const imgTagMatch = box.content.match(/<img[^>]*\ssrc=["']([^"']+)["'][^>]*>/i);
        const rawSrc = imgTagMatch ? imgTagMatch[1] : box.content;
        const src = rawSrc.startsWith('/') ? `${API}${rawSrc}` : rawSrc;
        return (
          <div style={{ marginTop: 10, borderRadius: 10, overflow: 'hidden', background: 'var(--bg-4)', cursor: 'zoom-in' }}
            onClick={e => { e.stopPropagation(); setLightbox({ images: [src, ...images], index: 0 }); }}>
            <img src={src} alt={box.title || ''} style={{ width: '100%', display: 'block', objectFit: 'cover' }} />
          </div>
        );
      }
      case 'video': {
        const yt = box.content.match(/(?:v=|youtu\.be\/)([^&?/]+)/);
        if (yt) return (
          <div style={{ marginTop: 10, borderRadius: 10, overflow: 'hidden', aspectRatio: '16/9' }}
            onClick={e => e.stopPropagation()}>
            <iframe src={`https://www.youtube.com/embed/${yt[1]}`} style={{ width: '100%', height: '100%', border: 'none' }} allowFullScreen title={box.title} />
          </div>
        );
        return <video src={box.content} controls style={{ width: '100%', marginTop: 10, borderRadius: 10 }} onClick={e => e.stopPropagation()} />;
      }
      case 'link':
        return <LinkPreview url={box.content} preview={linkPreview} accent={accent} />;
      default: return null;
    }
  };

  // All images for this card (box-level thumbnails only; image-type boxes handled above)
  const thumbImages = images;

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ delay: index * 0.035, duration: 0.22, ease: 'easeOut' }}
        ref={cardRef}
        onHoverStart={() => setHovered(true)}
        onHoverEnd={() => setHovered(false)}
        data-card="true"
        data-current-level={isCurrentLevel ? 'true' : undefined}
        onDragOver={onCardDragOver}
        onDragLeave={onCardDragLeave}
        onDrop={onCardDrop}
        style={{
          marginBottom: 0,
          background: cardDragOver ? `${accent}10` : box.color ? `${box.color}cc` : 'var(--card-bg)',
          border: `1px solid ${cardDragOver ? accent : isCurrentLevel ? accent : hovered ? accent + '55' : 'var(--border)'}`,
          borderRadius: 14, overflow: 'hidden',
          boxShadow: isCurrentLevel ? `0 0 0 2px ${accent}18, var(--card-shadow)` : hovered ? `var(--card-shadow-hover), 0 0 0 1px ${accent}20` : 'var(--card-shadow)',
          transition: 'border-color 0.15s, box-shadow 0.15s, background 0.15s',
          cursor: isCurrentLevel ? 'default' : 'pointer', position: 'relative',
        }}
      >
        {/* Accent bar — use darkened highlight when card is highlighted, else plan accent */}
        <div style={{ height: 3, background: isCurrentLevel
          ? `linear-gradient(90deg, ${accent}, ${accent}88)`
          : box.color
          ? `linear-gradient(90deg, ${darkenHex(box.color)}, ${darkenHex(box.color, 0.35)})`
          : `linear-gradient(90deg, ${accent}, ${accent}55)`
        }} />

        {/* Drop overlay hint */}
        <AnimatePresence>
          {cardDragOver && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              style={{
                position: 'absolute', inset: 0, zIndex: 10, pointerEvents: 'none',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: `${accent}08`,
              }}>
              <div style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
                padding: '12px 20px', borderRadius: 12,
                background: 'var(--toolbar-bg)', backdropFilter: 'blur(8px)',
                border: `1px solid ${accent}40`,
              }}>
                <Upload size={18} color={accent} />
                <span style={{ fontSize: 12, fontWeight: 600, color: accent }}>Add image or create file card</span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Body */}
        <div style={{ padding: compactView ? '10px 12px 12px' : '14px 15px 16px' }}>
          {/* Title is the drag handle; card actions stay clickable. */}
          <div
            style={{ display: 'flex', alignItems: 'flex-start', gap: 9, userSelect: 'none' }}>
            <div {...(dragHandleProps || {})} title={dragHandleProps ? 'Drag title to reorder' : undefined}
              style={{ flex: 1, minWidth: 0, cursor: dragHandleProps ? 'grab' : 'default', touchAction: dragHandleProps ? 'none' : undefined }}>
              {isCurrentLevel && <div style={{ fontSize: 10, fontWeight: 700, color: accent, letterSpacing: '0.5px', textTransform: 'uppercase', marginBottom: 5 }}>Current level</div>}
              {box.title && <h3 style={{ fontSize: 14, fontWeight: 700, lineHeight: 1.35, color: 'var(--text-primary)', wordBreak: 'break-word' }}>{box.title}</h3>}
              {!!box.is_private && <span title="Only the plan owner can see this card and its subcards" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 4, fontSize: 10, fontWeight: 700, color: '#a16207' }}><LockKeyhole size={11} /> Private</span>}
              {privacyError && <span role="alert" style={{ display: 'block', color: '#ef4444', fontSize: 11, marginTop: 4 }}>{privacyError}</span>}
              {lockedBy && <span title={`Being edited by ${lockedBy}`} style={{ fontSize: 10, color: '#a16207' }}>🔒 {lockedBy} is editing</span>}
            </div>

            {/* Actions — single ⋯ button with dropdown */}
            {(onEdit || onDelete || onAskAI || onToggleSpan || canSetPrivate) && (
              <div
                onClick={e => e.stopPropagation()}
                style={{ position: 'relative', flexShrink: 0, opacity: showActions || menuOpen ? 1 : 0, transition: 'opacity 0.15s' }}
              >
                <ActionBtn
                  ref={menuBtnRef}
                  onClick={openMenu}
                  title="Card options"
                  active={menuOpen}
                >
                  <MoreHorizontal size={13} />
                </ActionBtn>

                {/* Dropdown — portalled to body to escape card overflow:hidden */}
                {menuOpen && ReactDOM.createPortal(
                  <div
                    ref={menuRef}
                    onClick={e => e.stopPropagation()}
                    style={{
                      position: 'fixed', top: menuPos.top, right: menuPos.right, zIndex: 9999,
                      background: 'var(--bg-2)', border: '1px solid var(--border)',
                      borderRadius: 10, boxShadow: '0 8px 32px rgba(0,0,0,0.22)',
                      minWidth: 170, padding: '5px 0',
                      animation: 'fadeInDown 0.12s ease',
                    }}>

                    {/* Highlight row */}
                    <div style={{ padding: '6px 12px 4px', borderBottom: '1px solid var(--border)', marginBottom: 4 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-faint)', letterSpacing: '0.5px', marginBottom: 7 }}>HIGHLIGHT</div>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {HIGHLIGHT_COLORS.map(hc => {
                          const isActive = (box.color === hc.value) || (!box.color && hc.value === null);
                          return (
                            <button
                              key={hc.label}
                              title={hc.label}
                              onClick={e => { e.stopPropagation(); handleHighlight(hc.value); }}
                              style={{
                                width: 20, height: 20, borderRadius: '50%', cursor: 'pointer', padding: 0,
                                background: hc.swatch === 'transparent' ? 'var(--bg-4)' : hc.swatch,
                                border: isActive ? `2.5px solid ${hc.border}` : `1.5px solid ${hc.border}80`,
                                boxShadow: isActive ? `0 0 0 1.5px var(--bg-2)` : 'none',
                                transform: isActive ? 'scale(1.15)' : 'scale(1)',
                                transition: 'transform 0.1s, box-shadow 0.1s',
                                position: 'relative',
                              }}
                            >
                              {hc.value === null && (
                                <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: 'var(--text-faint)' }}>✕</span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Menu items */}
                    {onEdit && (
                      <MenuItem
                        icon={box.is_todo ? <CheckSquare size={13} color="#10b981" /> : <Square size={13} />}
                        label={box.is_todo ? 'Remove TODO' : 'Mark as TODO'}
                        onClick={handleToggleTodo}
                        active={box.is_todo}
                      />
                    )}
                    {canSetPrivate && (
                      <MenuItem icon={box.is_private ? <LockKeyholeOpen size={13} /> : <LockKeyhole size={13} />}
                        label={box.is_private ? 'Remove private setting' : 'Make private (includes subcards)'}
                        onClick={handleTogglePrivate} active={box.is_private} />
                    )}
                    {onAskAI && (
                      <MenuItem icon={<Sparkles size={13} color="#8b5cf6" />} label="Ask AI" onClick={() => { setMenuOpen(false); onAskAI(box); }} />
                    )}
                    {onEdit && (
                      <MenuItem icon={<Edit2 size={13} />} label="Edit" onClick={() => { setMenuOpen(false); onEdit(); }} />
                    )}
                    {onEdit && (
                      <MenuItem icon={<History size={13} />} label="Version history" onClick={() => { setMenuOpen(false); setShowHistory(true); }} />
                    )}
                    {onToggleSpan && (
                      <MenuItem
                        icon={(box.span || 1) > 1 ? <Minimize2 size={13} /> : <Maximize2 size={13} />}
                        label={(box.span || 1) > 1 ? 'Single width' : 'Full width'}
                        onClick={() => { setMenuOpen(false); onToggleSpan(); }}
                      />
                    )}
                    {onDelete && (
                      <MenuItem icon={<Trash2 size={13} color="#ef4444" />} label="Delete" danger onClick={() => { setMenuOpen(false); handleDelete({ stopPropagation: () => {} }); }} />
                    )}
                  </div>,
                  document.body
                )}
              </div>
            )}
          </div>

          {/* Cards with written content show a summary; image-only cards keep a visual preview. */}
          {showCompactSummary ? (
            <div id={`card-preview-${box.id}`} style={{ fontSize: 12.5, lineHeight: 1.6, color: 'var(--text-muted)', marginTop: 10 }}>
              {box.compact_summary_status === 'ready' && box.compact_summary
                ? compactSummaryPreview(box.compact_summary)
                : box.compact_summary_status === 'failed' ? 'Summary unavailable. Expand to read this card.'
                  : box.compact_summary_status === 'updating' ? 'Creating summary…' : 'Summary pending…'}
            </div>
          ) : (
            <div id={`card-preview-${box.id}`} style={previewClipped ? { maxHeight: COMPACT_PREVIEW_HEIGHT, overflow: 'hidden', pointerEvents: 'none' } : undefined}>
            <div ref={previewRef}>
          {/* Main content */}
          {renderContent()}

          {/* ── Thumbnail strip ─────────────────────────────────────────────── */}
          {thumbImages.length > 0 && (
            <div style={{ marginTop: 12 }} onClick={e => e.stopPropagation()}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: `repeat(${Math.min(thumbImages.length, 3)}, 1fr)`,
                gap: 6,
                padding: '4px 0',
              }}>
                {thumbImages.map((imgSrc, i) => (
                  <div
                    key={i}
                    draggable
                    onDragStart={e => { e.stopPropagation(); e.dataTransfer.setData('text/x-thumb-index', i); }}
                    onDragOver={e => { e.preventDefault(); e.stopPropagation(); }}
                    onDrop={async e => {
                      e.preventDefault(); e.stopPropagation();
                      // Thumb reorder
                      const from = parseInt(e.dataTransfer.getData('text/x-thumb-index'));
                      if (!isNaN(from) && from !== i) {
                        const reordered = [...thumbImages];
                        const [moved] = reordered.splice(from, 1);
                        reordered.splice(i, 0, moved);
                        fetch(`${API}/api/boxes/${box.id}`, {
                          method: 'PUT', headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
                          body: JSON.stringify({ images: reordered }),
                        }).then(r => r.json()).then(updated => onUpdate && onUpdate({ ...updated, images: reordered }));
                        return;
                      }
                      // External file dropped onto an existing image — append it
                      if (!token) return;
                      const files = [...e.dataTransfer.files].filter(f => f.type.startsWith('image/'));
                      if (!files.length) return;
                      setUploading(true);
                      const newImages = [...thumbImages];
                      for (const file of files) {
                        const form = new FormData(); form.append('file', file);
                        try {
                          const { url } = await fetch(`${API}/api/upload`, { method: 'POST', body: form, headers: token ? { Authorization: `Bearer ${token}` } : {} }).then(r => r.json());
                          newImages.push(url.startsWith('/') ? `${API}${url}` : url);
                        } catch (err) { console.error('[BoxCard] Upload failed', err); }
                      }
                      const res = await fetch(`${API}/api/boxes/${box.id}`, {
                        method: 'PUT', headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
                        body: JSON.stringify({ images: newImages }),
                      });
                      const updated = await res.json();
                      setUploading(false);
                      if (onUpdate) onUpdate({ ...updated, images: newImages });
                    }}
                    onMouseEnter={e => { const btn = e.currentTarget.querySelector('.thumb-del'); if (btn) btn.style.opacity = '1'; }}
                    onMouseLeave={e => { const btn = e.currentTarget.querySelector('.thumb-del'); if (btn) btn.style.opacity = '0'; }}
                    style={{
                      borderRadius: 7, overflow: 'hidden', cursor: 'grab',
                      aspectRatio: '1', background: 'var(--bg-4)', position: 'relative',
                    }}
                  >
                    <img
                      src={imgSrc} alt=""
                      onClick={() => setLightbox({ images: thumbImages, index: i })}
                      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', cursor: 'zoom-in' }}
                    />
                    {/* Delete button — hidden until hover */}
                    <button
                      className="thumb-del"
                      onClick={e => {
                        e.stopPropagation();
                        const updated = thumbImages.filter((_, idx) => idx !== i);
                        fetch(`${API}/api/boxes/${box.id}`, {
                          method: 'PUT', headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
                          body: JSON.stringify({ images: updated }),
                        }).then(r => r.json()).then(res => onUpdate && onUpdate({ ...res, images: updated }));
                      }}
                      style={{
                        position: 'absolute', top: 4, right: 4,
                        width: 20, height: 20, borderRadius: '50%',
                        background: 'rgba(0,0,0,0.6)', border: 'none',
                        color: 'white', cursor: 'pointer', fontSize: 12,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        lineHeight: 1, padding: 0,
                        opacity: 0, transition: 'opacity 0.15s',
                      }}
                    >
                      <X size={10} />
                    </button>
                    {i === 5 && thumbImages.length > 6 && (
                      <div style={{
                        position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.55)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: 'white', fontSize: 14, fontWeight: 700,
                      }}>
                        +{thumbImages.length - 6}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}



          {uploading && (
            <div style={{ marginTop: 8, fontSize: 11, color: accent, display: 'flex', alignItems: 'center', gap: 6 }}>
              <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 0.8, ease: 'linear' }}>
                <Upload size={11} />
              </motion.div>
              Uploading…
            </div>
          )}

          {/* Child badges */}
          {box.childPreviews?.length > 0 && (
            <div style={{ marginTop: 14, borderTop: '1px solid var(--border)', paddingTop: 10, display: 'flex', flexWrap: 'wrap', gap: 5 }}>
              {box.childPreviews.map((child, i) => (
                <div key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 10px', borderRadius: 20, background: `${accent}10`, border: `1px solid ${accent}22`, fontSize: 11, color: 'var(--text-muted)', fontWeight: 500, maxWidth: 160, overflow: 'hidden' }}>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{child.title || 'Untitled'}</span>
                </div>
              ))}
            </div>
          )}

            </div>
            </div>
          )}

          {compactView && canExpand && (
            <button
              type="button"
              data-no-drill
              aria-expanded={expanded}
              aria-controls={`card-preview-${box.id}`}
              onClick={e => { e.stopPropagation(); setExpanded(value => !value); }}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 7,
                padding: '3px 0', border: 'none', background: 'transparent', color: accent,
                fontSize: 11, fontWeight: 650, cursor: 'pointer', fontFamily: 'inherit' }}
            >
              {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
              {expanded ? 'Collapse' : 'Expand'}
            </button>
          )}

          {/* TODO badge — bottom right */}
          {!!box.is_todo && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: '2px 8px', borderRadius: 5,
                background: 'var(--todo-badge-bg, #111)',
                color: 'var(--todo-badge-text, #fff)',
              }}>
                <CheckSquare size={10} />
                <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.4px' }}>TODO</span>
              </div>
            </div>
          )}
        </div>
      </motion.div>

      {showHistory && <CardVersionHistory box={box} token={token} onUpdate={onUpdate} onClose={() => setShowHistory(false)} />}

      {/* ── Lightbox ──────────────────────────────────────────────────────── */}
      <AnimatePresence>
        {showDeleteModal && (
        <div onClick={e => e.stopPropagation()} style={{ position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: 14, padding: '24px 28px', maxWidth: 340, width: '90%', boxShadow: '0 24px 80px rgba(0,0,0,0.4)' }}>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8, color: 'var(--text-primary)' }}>Delete card?</div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 20 }}><strong style={{ color: 'var(--text-primary)' }}>{box.title || 'Untitled'}</strong> and all its children will be permanently deleted.</div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button data-no-drill onClick={() => setShowDeleteModal(false)} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-3)', color: 'var(--text-primary)', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>Cancel</button>
              <button data-no-drill data-testid="confirm-delete" onClick={() => { setShowDeleteModal(false); onDelete(); }} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: '#e63946', color: '#fff', cursor: 'pointer', fontWeight: 700, fontSize: 13 }}>Delete</button>
            </div>
          </div>
        </div>
      )}
      {lightbox && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setLightbox(null)}
            style={{
              position: 'fixed', inset: 0, zIndex: 1000,
              background: 'rgba(0,0,0,0.92)', backdropFilter: 'blur(8px)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            {/* Close */}
            <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
              onClick={() => setLightbox(null)}
              style={{ position: 'absolute', top: 20, right: 20, background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 10, padding: 8, cursor: 'pointer', color: 'white', display: 'flex', zIndex: 10 }}>
              <X size={18} />
            </motion.button>

            {/* Prev */}
            {lightbox.index > 0 && (
              <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
                onClick={e => { e.stopPropagation(); setLightbox(l => ({ ...l, index: l.index - 1 })); }}
                style={{ position: 'absolute', left: 20, background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 10, padding: 10, cursor: 'pointer', color: 'white', display: 'flex' }}>
                <ChevronLeft size={20} />
              </motion.button>
            )}

            {/* Image */}
            <motion.img
              key={lightbox.index}
              initial={{ opacity: 0, scale: 0.93 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.18 }}
              src={lightbox.images[lightbox.index]}
              alt=""
              onClick={e => e.stopPropagation()}
              style={{ maxWidth: '90vw', maxHeight: '88vh', objectFit: 'contain', borderRadius: 12, boxShadow: '0 24px 80px rgba(0,0,0,0.6)' }}
            />

            {/* Next */}
            {lightbox.index < lightbox.images.length - 1 && (
              <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
                onClick={e => { e.stopPropagation(); setLightbox(l => ({ ...l, index: l.index + 1 })); }}
                style={{ position: 'absolute', right: 20, background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 10, padding: 10, cursor: 'pointer', color: 'white', display: 'flex' }}>
                <ChevronRightIcon size={20} />
              </motion.button>
            )}

            {/* Counter */}
            {lightbox.images.length > 1 && (
              <div style={{ position: 'absolute', bottom: 20, left: '50%', transform: 'translateX(-50%)', color: 'rgba(255,255,255,0.5)', fontSize: 13, fontWeight: 500 }}>
                {lightbox.index + 1} / {lightbox.images.length}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function LinkPreview({ url, preview, accent }) {
  return (
    <a href={url} target="_blank" rel="noopener noreferrer"
      onClick={e => e.stopPropagation()}
      style={{ display: 'block', marginTop: 10, textDecoration: 'none', borderRadius: 10, overflow: 'hidden', border: '1px solid var(--border)', background: 'var(--bg-4)' }}>
      {!preview ? (
        <div className="skeleton" style={{ height: 100 }} />
      ) : preview.image ? (
        <img src={preview.image} alt={preview.title || ''} style={{ width: '100%', maxHeight: 180, objectFit: 'cover', display: 'block' }} onError={e => { e.target.style.display = 'none'; }} />
      ) : (
        <div style={{ height: 64, display: 'flex', alignItems: 'center', justifyContent: 'center', background: `${accent}10` }}>
          {preview.favicon
            ? <img src={preview.favicon} alt="" style={{ width: 28, height: 28, borderRadius: 5 }} onError={e => { e.target.style.display = 'none'; }} />
            : <ExternalLink size={22} color={accent} style={{ opacity: 0.4 }} />}
        </div>
      )}
      <div style={{ padding: '9px 12px' }}>
        {preview ? (
          <>
            {preview.title && <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{preview.title}</div>}
            {preview.description && <div style={{ fontSize: 11.5, color: 'var(--text-muted)', lineHeight: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{preview.description}</div>}
            <div style={{ fontSize: 11, color: 'var(--text-faint)', marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
              <ExternalLink size={10} />{preview.domain || url}
            </div>
          </>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <div className="skeleton" style={{ height: 12, width: '60%' }} />
            <div className="skeleton" style={{ height: 10, width: '90%' }} />
          </div>
        )}
      </div>
    </a>
  );
}

function MenuItem({ icon, label, onClick, danger }) {
  const [hov, setHov] = React.useState(false);
  return (
    <button
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 9,
        width: '100%', padding: '7px 14px',
        background: hov ? (danger ? 'rgba(239,68,68,0.08)' : 'var(--bg-4)') : 'transparent',
        border: 'none', cursor: 'pointer', textAlign: 'left',
        color: danger ? '#ef4444' : 'var(--text-primary)',
        fontSize: 13, fontFamily: 'inherit', fontWeight: 500,
        transition: 'background 0.1s',
      }}
    >
      <span style={{ color: danger ? '#ef4444' : 'var(--text-muted)', display: 'flex' }}>{icon}</span>
      {label}
    </button>
  );
}

const ActionBtn = React.forwardRef(function ActionBtn({ onClick, title, danger, active, ai, children }, ref) {
  return (
    <motion.button ref={ref} whileHover={{ scale: 1.12 }} whileTap={{ scale: 0.9 }} onClick={onClick} title={title}
      style={{
        background: danger ? 'rgba(239,68,68,0.14)' : ai ? 'rgba(139,92,246,0.14)' : active ? 'rgba(99,102,241,0.15)' : 'var(--bg-4)',
        border: `1px solid ${danger ? 'rgba(239,68,68,0.28)' : ai ? 'rgba(139,92,246,0.4)' : active ? 'rgba(99,102,241,0.4)' : 'var(--border)'}`,
        borderRadius: 6, padding: 5, cursor: 'pointer',
        color: danger ? '#ef4444' : ai ? '#8b5cf6' : 'var(--text-muted)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s',
      }}>
      {children}
    </motion.button>
  );
});

const fileButton = {
  display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 8px', borderRadius: 7,
  border: '1px solid var(--border)', background: 'var(--bg-4)', color: 'var(--text-primary)',
  cursor: 'pointer', fontSize: 12, fontFamily: 'inherit',
};
